import { type CopyParams } from "types/i18n/CopyParams";
import { type ICopyResolver } from "types/i18n/ICopyResolver";
import { type LocalizedPluralText } from "types/i18n/LocalizedPluralText";
import { type LocalizedText } from "types/i18n/LocalizedText";
import { type SupportedLocale } from "types/i18n/SupportedLocaleEnum";

import { interpolateCopy } from "./interpolateCopy";

const pluralRulesByLocale = new Map<SupportedLocale, Intl.PluralRules>();

/**
 * A copy leaf's slot for one language, placeholders filled when `params` are given.
 *
 * @param params.text - The copy leaf.
 * @param params.locale - The language to read.
 * @param params.params - Values for the slot's `{{name}}` placeholders.
 * @returns The rendered text.
 * @throws When the slot has a placeholder `params` does not fill.
 */
export function resolveCopyText({
  text,
  locale,
  params,
}: {
  text: LocalizedText;
  locale: SupportedLocale;
  params?: CopyParams;
}): string {
  return interpolateCopy({ template: text[locale], params: params });
}

/**
 * The form of a count-dependent copy leaf that `count` selects in one language, with `{{count}}` filled.
 *
 * @param params.text - The plural copy leaf.
 * @param params.locale - The language to read.
 * @param params.count - The number the text describes.
 * @param params.params - Values for any other placeholders.
 * @returns The rendered text.
 * @throws When the form has a placeholder neither `count` nor `params` fills.
 */
export function resolveCopyPlural({
  text,
  locale,
  count,
  params,
}: {
  text: LocalizedPluralText;
  locale: SupportedLocale;
  count: number;
  params?: CopyParams;
}): string {
  return interpolateCopy({
    template: selectPluralTemplate({ text: text, locale: locale, count: count }),
    params: { ...params, count: count },
  });
}

/**
 * The unfilled form of a count-dependent copy leaf that `count` selects in one language.
 *
 * @param params.text - The plural copy leaf.
 * @param params.locale - The language to read.
 * @param params.count - The number the text describes.
 * @returns The form's template, placeholders intact.
 */
export function selectPluralTemplate({
  text,
  locale,
  count,
}: {
  text: LocalizedPluralText;
  locale: SupportedLocale;
  count: number;
}): string {
  const forms = text[locale];
  const category = getPluralRules({ locale: locale }).select(count);
  return (category === "one" ? forms.one : undefined) ?? forms.other;
}

/**
 * Binds the copy readers to one language.
 *
 * @param params.locale - The language every method renders in.
 * @returns A resolver for that language.
 */
export function createCopyResolver({ locale }: { locale: SupportedLocale }): ICopyResolver {
  return {
    locale: locale,
    text: (text) => resolveCopyText({ text: text, locale: locale }),
    format: ({ text, params }) => resolveCopyText({ text: text, locale: locale, params: params }),
    plural: ({ text, count, params }) =>
      resolveCopyPlural({ text: text, locale: locale, count: count, params: params }),
  };
}

function getPluralRules({ locale }: { locale: SupportedLocale }): Intl.PluralRules {
  const cachedRules = pluralRulesByLocale.get(locale);
  if (cachedRules) {
    return cachedRules;
  }
  const pluralRules = new Intl.PluralRules(locale);
  pluralRulesByLocale.set(locale, pluralRules);
  return pluralRules;
}
